import type { NavigationDecision, NavigationDenyReason, ProductKey } from '@traceio-org/platform-contracts';
import type { BeginSignInOptions, ReauthenticateReason, SessionMode } from '@traceio-org/auth-ui';
import { checkDestination, type DestinationPolicy } from '../security/destination';
import { RETURN_TO_PARAM, validReturnPath } from '../security/return-path';

/** Message the login page shows after a reauthenticate decision it cannot act on by itself. */
export type LoginNotice =
  | { kind: 'reauthenticate'; reason: ReauthenticateReason; connectionHint?: string }
  | { kind: 'reauthenticate-loop'; reason: ReauthenticateReason };

export type UnavailableReason = 'not_deployed' | 'disabled_in_environment' | 'destination_refused';

/** Router state the hub passes between its pages. Every field is re-validated by the page that reads it. */
export interface HubLocationState {
  productKey?: ProductKey;
  returnPath?: string;
  contextId?: string;
  notice?: LoginNotice;
  unavailableReason?: UnavailableReason;
  denyReason?: NavigationDenyReason;
  /** Set on the page a sign-in returned to when that sign-in answered a reauthenticate requirement. */
  reauthenticated?: ReauthenticateReason;
  /** A silent sign-in (prompt none) already came back without a session: the page shows its methods. */
  silentTried?: boolean;
  /**
   * Set only by the callback, with silentTried, when that silent attempt failed. Trace Admin uses it to continue
   * straight to the interactive sign-in (Auth0's MFA prompt) instead of showing its one button.
   */
  silentFailed?: boolean;
}

export type DecisionPlan =
  | { type: 'redirect'; url: string }
  | { type: 'navigate'; to: string; state?: HubLocationState; replace: boolean }
  | { type: 'sign-in'; options: BeginSignInOptions };

export interface DecisionContext {
  /** Product the person asked for (from the route or the hub state). */
  productKey?: ProductKey | undefined;
  returnPath?: string | undefined;
  contextId?: string | undefined;
  /** How the person signed in. */
  mode: SessionMode;
  /** Set when the current sign-in was itself started by a reauthenticate decision. */
  reauthenticated?: ReauthenticateReason | undefined;
  policy: DestinationPolicy;
  /** Replace the current history entry (for example /auth/callback) instead of pushing. */
  replace?: boolean;
}

export function loginPath(productKey: ProductKey | null | undefined, returnPath: string | undefined): string {
  const path = productKey ? `/login/${productKey}` : '/login';
  return returnPath ? `${path}?${new URLSearchParams({ [RETURN_TO_PARAM]: returnPath }).toString()}` : path;
}

function defined<T extends object>(value: T): T {
  return Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as T;
}

/**
 * Turns a platform-api navigation decision into exactly one next step. Pure, so every branch is unit
 * tested; useFollowDecision executes the plan. Rules that matter for security:
 * - a redirect leaves only for an allowed origin (checkDestination), otherwise the unavailable state;
 * - return paths from the server are re-validated before they are carried further;
 * - a reauthenticate decision that repeats right after a reauthentication never restarts sign-in again.
 */
export function planDecision(decision: NavigationDecision, ctx: DecisionContext): DecisionPlan {
  const replace = ctx.replace ?? false;
  switch (decision.kind) {
    case 'redirect': {
      const check = checkDestination(decision.url, decision.productKey, ctx.policy);
      if (check.ok) return { type: 'redirect', url: check.url };
      return {
        type: 'navigate',
        to: `/unavailable?product=${decision.productKey}`,
        state: { unavailableReason: 'destination_refused' },
        replace: true,
      };
    }
    case 'select-organization':
      return {
        type: 'navigate',
        to: '/select-organization',
        state: defined({
          productKey: decision.productKey ?? ctx.productKey,
          returnPath: validReturnPath(decision.returnPath) ?? ctx.returnPath,
        }),
        replace,
      };
    case 'select-product':
      return {
        type: 'navigate',
        to: '/select-product',
        state: defined({
          contextId: decision.contextId,
          returnPath: validReturnPath(decision.returnPath) ?? ctx.returnPath,
        }),
        replace,
      };
    case 'access-denied': {
      const productKey = decision.productKey ?? ctx.productKey;
      const query = new URLSearchParams(defined({ reason: decision.reason, product: productKey }) as Record<string, string>);
      return {
        type: 'navigate',
        to: `/access-denied?${query.toString()}`,
        state: defined({ contextId: decision.contextId ?? ctx.contextId, productKey }),
        replace,
      };
    }
    case 'no-workspace':
      return { type: 'navigate', to: '/no-workspace', replace };
    case 'unavailable':
      return {
        type: 'navigate',
        to: `/unavailable?product=${decision.productKey}`,
        state: { unavailableReason: decision.reason },
        replace,
      };
    case 'reauthenticate': {
      const productKey = decision.productKey ?? ctx.productKey;
      if (ctx.reauthenticated === decision.reason) {
        return {
          type: 'navigate',
          to: loginPath(productKey, ctx.returnPath),
          state: { notice: { kind: 'reauthenticate-loop', reason: decision.reason } },
          replace,
        };
      }
      if (ctx.mode === 'development') {
        // Development identity cannot switch connections or run MFA: explain it next to the fixtures.
        return {
          type: 'navigate',
          to: loginPath(productKey, ctx.returnPath),
          state: {
            notice: defined({
              kind: 'reauthenticate' as const,
              reason: decision.reason,
              connectionHint: decision.connectionHint,
            }),
          },
          replace,
        };
      }
      return {
        type: 'sign-in',
        options: defined({
          productKey,
          returnPath: ctx.returnPath,
          contextId: decision.contextId ?? ctx.contextId,
          organization: decision.organizationHint,
          connection: decision.connectionHint,
          // No prompt=login for mfa_required: auth-ui asks for MFA through acr_values and the tenant's
          // trace-mfa-policy Action challenges whenever the last MFA is too old, so a forced login only showed
          // the Google account chooser a second time (hosted dev, 2026-10-07).
          reauthenticate: decision.reason,
        }),
      };
    }
  }
}
